/**
 * `_shell` — the pathless layout route every signed-in screen sits under.
 *
 * Pathless (the leading underscore) because the chrome is not an address: the dashboard is `/`,
 * the client list `/klienci` and the plan library `/plans`. What the route adds is a shared layout
 * and one shared guard.
 *
 * ── The guard moved up, and that is the point ────────────────────────────────────────────────
 * Before this file, each signed-in route repeated `sessionRedirectFrom` in its own `beforeLoad`.
 * With two such routes that is duplication; with the builder, the calendar and the exercise
 * library it is a place to forget. The guard runs once here and every child inherits it, so a
 * new screen behind the gate is a route file with a component and nothing else.
 *
 * The redirect is asked about `'/'` rather than about the child's own path. That is deliberate
 * and matches what the session's routing table actually models: `sessionRedirectFrom` answers
 * "does this session belong in the app, or somewhere else" — `/sign-in`, `/refused`,
 * `/complete-profile` — and every route under this shell is "in the app". A child path is not a
 * `SessionRoute` and could not be passed anyway.
 *
 * ── It also holds the first frame for the trainer's colour ───────────────────────────────────
 * After the guard, `holdForBrandPaint` waits (at most a second) for the brand colour of an account
 * this browser has never painted, so the shell appears in that colour instead of appearing in Flame
 * and repainting. `useBrandPaint` then lays it on. Both are `app/lib/brandPaint.ts`.
 */

import { useEffect } from 'react';

import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';

import { sessionRedirectFrom } from '~/entities/session';
import { claimParkedTrialCode, parkTrialCodeFrom } from '~/pages/account';
import { initClarityRecording } from '~/shared/lib/clarityRecording';
import { holdForBrandPaint, useBrandPaint } from '~/app/lib/brandPaint';
import { AppShell } from '~/app/ui/AppShell';

export const Route = createFileRoute('/_shell')({
  beforeLoad: async ({ location }) => {
    const destination = sessionRedirectFrom('/');
    // `throw: true` makes `redirect` raise the router's own Response itself. A bare `throw
    // redirect(...)` throws a non-Error, which is exactly what `only-throw-error` is there to catch.
    if (destination !== null) {
      // A trial-code link (subscription R19) outlives the sign-in it is sent to — `pages/account`.
      parkTrialCodeFrom(location.pathname, location.search);
      redirect({ to: destination, replace: true, throw: true });
    }
    const parked = claimParkedTrialCode(location.pathname, location.search);
    if (parked !== null) redirect({ to: '/konto', search: { code: parked }, replace: true, throw: true });
    await holdForBrandPaint();
  },
  component: ShellLayout,
});

function ShellLayout() {
  useBrandPaint();

  // Behind the session guard on purpose: Clarity (staging-only, see clarityRecording.ts) must
  // never see sign-in/sign-up — mounting here is the screen-level exclusion, same as mobile.
  useEffect(() => {
    initClarityRecording();
  }, []);

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
